import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Stack, Button, Table, TableBody,
    TableCell, TableHead, TableRow, Divider,
    CircularProgress, Breadcrumbs, Grid, Chip
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import { getApiUrl, getUploadUrl } from '../config/api';

const ReceiptDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [receipt, setReceipt] = useState<any>(null);
    const [school, setSchool] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const userInfo = localStorage.getItem('userInfo');
                const token = userInfo ? JSON.parse(userInfo).token : null;
                const config = { headers: { Authorization: `Bearer ${token}` } };

                const [receiptRes, schoolRes] = await Promise.all([
                    axios.get(getApiUrl(`receipts/${id}`), config),
                    axios.get(getApiUrl('school'), config)
                ]);

                setReceipt(receiptRes.data);
                setSchool(schoolRes.data);
            } catch (error) {
                console.error('Fetch receipt detail error:', error);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchData();
    }, [id]);

    const handleResendEmail = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;

            if (!token) {
                alert('Authentication token not found. Please log in again.');
                return;
            }

            await axios.post(getApiUrl(`receipts/${id}/email`), {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Receipt email resent successfully!');
        } catch (error) {
            console.error('Error resending email:', error);
            alert('Failed to resend receipt email.');
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>;
    if (!receipt) return <Typography sx={{ p: 4 }}>Receipt not found</Typography>;

    return (
        <Box sx={{
            p: { xs: 2, md: 4 },
            bgcolor: { xs: 'white', md: '#F4F7FE' },
            minHeight: 'calc(100vh - 64px)',
            '@media print': { p: 0, bgcolor: 'white' }
        }}>
            {/* Header / Actions - Hidden on Print */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                mb={4}
                spacing={2}
                className="no-print"
            >
                <Breadcrumbs separator="/" aria-label="breadcrumb">
                    <Typography
                        color="text.secondary"
                        sx={{ fontSize: '0.8rem', cursor: 'pointer', fontWeight: 700 }}
                        onClick={() => navigate('/dashboard/payments')}
                    >
                        Payments
                    </Typography>
                    <Typography color="#F9B43F" sx={{ fontSize: '0.8rem', fontWeight: 800 }}>{receipt.receiptNo}</Typography>
                </Breadcrumbs>

                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleResendEmail}
                        sx={{ color: '#263B7C', borderColor: '#263B7C', fontWeight: 700, borderRadius: '8px' }}
                    >
                        EMAIL
                    </Button>
                    {receipt.invoice?.student?.guardian?.phone && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                const message = `Hello ${receipt.invoice.student.guardian.firstName}, thank you for your payment of $${Number(receipt.amount).toLocaleString()} for ${receipt.invoice.student.firstName}. Receipt: ${receipt.receiptNo}.`;
                                window.open(`https://wa.me/${receipt.invoice.student.guardian.phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
                            }}
                            sx={{ color: '#25D366', borderColor: '#25D366', fontWeight: 700, borderRadius: '8px' }}
                        >
                            WHATSAPP
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<PrintIcon />}
                        onClick={() => window.print()}
                        sx={{ bgcolor: '#263B7C', fontWeight: 700, borderRadius: '8px' }}
                    >
                        PRINT
                    </Button>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        color="inherit"
                        size="small"
                        sx={{ fontWeight: 700 }}
                    >
                        Back
                    </Button>
                </Stack>
            </Stack>

            {/* Receipt Document */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: { xs: 0, md: '24px' },
                    p: { xs: 3, sm: 6, md: 8 },
                    maxWidth: 900,
                    mx: 'auto',
                    border: { xs: 'none', md: '1px solid #edf2f7' },
                    boxShadow: { xs: 'none', md: '0 10px 40px rgba(0,0,0,0.02)' },
                    '@media print': {
                        p: 0,
                        boxShadow: 'none',
                        width: '100%',
                        maxWidth: '100%',
                        border: 'none',
                        borderRadius: 0
                    },
                    pb: 8
                }}
                className="print-area"
            >
                {/* Header Section */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'center', sm: 'flex-start' }}
                    mb={6}
                    spacing={4}
                    sx={{ textAlign: { xs: 'center', sm: 'left' } }}
                >
                    <Box>
                        {school?.logo && (
                            <Box
                                component="img"
                                src={getUploadUrl(school.logo)}
                                sx={{ height: 75, mb: 2, objectFit: 'contain' }}
                                alt="School Logo"
                            />
                        )}
                        <Typography variant="h6" fontWeight={900} color="#263B7C" sx={{ letterSpacing: -0.5 }}>
                            {school?.name || 'School Name'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 1, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                            {school?.address}
                            {'\n'}{school?.phone} | {school?.email}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                        <Typography variant="h4" fontWeight={900} color="#F1F4F9" sx={{ letterSpacing: 2, mb: -1, display: { xs: 'none', md: 'block' } }}>
                            RECEIPT
                        </Typography>
                        <Typography variant="h6" fontWeight={900} color="#38A169" sx={{ letterSpacing: 1, mt: { md: 1 } }}>
                            OFFICIAL RECEIPT
                        </Typography>
                        <Stack spacing={1} mt={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>Receipt No</Typography>
                                <Typography variant="subtitle1" fontWeight={800} color="#2D3748">{receipt.receiptNo}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase' }}>Date Paid</Typography>
                                <Typography fontWeight={700}>{new Date(receipt.paidAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Stack>

                <Divider sx={{ mb: 6, borderColor: '#edf2f7' }} />

                {/* Received From & Payment Method */}
                <Grid container spacing={4} mb={6}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5, fontSize: '0.65rem' }}>Received From</Typography>
                        <Typography variant="subtitle1" fontWeight={900} color="#2D3748">
                            {receipt.invoice.student.firstName} {receipt.invoice.student.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                            Student ID: {receipt.invoice.student.studentId}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>Payment Method</Typography>
                        <Chip
                            label={receipt.method.replace('_', ' ')}
                            sx={{
                                fontWeight: 900,
                                borderRadius: '8px',
                                bgcolor: '#F0FFF4',
                                color: '#2F855A',
                                border: '1px solid #C6F6D5',
                                fontSize: '0.75rem'
                            }}
                        />
                        {receipt.reference && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase' }}>Reference</Typography>
                                <Typography fontWeight={700}>{receipt.reference}</Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>

                {/* Payment Details Table */}
                <Box sx={{ overflowX: 'auto', mb: 6, mx: { xs: -3, sm: 0 } }}>
                    <Table sx={{ minWidth: 600 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#38A169', '@media print': { bgcolor: '#38A169 !important', printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' } }}>
                                <TableCell sx={{ fontWeight: 800, color: 'white', border: 0 }}>DESCRIPTION</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, color: 'white', border: 0 }}>AMOUNT PAID</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow sx={{ bgcolor: 'white' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#4A5568' }}>
                                    Payment for Invoice #{receipt.invoice.invoiceNo}
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Applied to Academic Tuition & Fees
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 900, color: '#2D3748', fontSize: '1.2rem' }}>
                                    ${Number(receipt.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>

                {/* Amount in Words (Optional but adds premium feel) */}
                <Box sx={{ mb: 6, p: 2, bgcolor: '#F8FAFC', borderRadius: '12px', border: '1px solid #edf2f7' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase', display: 'block', mb: 0.5, fontSize: '0.65rem' }}>Amount Received</Typography>
                    <Typography variant="h6" fontWeight={900} color="#38A169">
                        ${Number(receipt.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </Typography>
                </Box>

                {/* Footer Section */}
                <Box sx={{ mt: 10, pt: 4, borderTop: '2px dashed #edf2f7', textAlign: 'center' }}>
                    <Typography variant="subtitle2" fontWeight={800} color="#2D3748" mb={1}>
                        Thank you for your payment!
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, maxWidth: 400, display: 'block', mx: 'auto' }}>
                        This is a computer-generated official receipt.
                        No signature is required.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 4, opacity: 0.5 }}>
                        Generated on {new Date().toLocaleString()} • Powered by SquareEdge SSAS
                    </Typography>
                </Box>
            </Paper>

            <style>
                {`
                    @media print {
                        .no-print { display: none !important; }
                        body { background: white !important; margin: 0 !important; }
                        .print-area { width: 100% !important; margin: 0 !important; border: none !important; }
                        table { width: 100% !important; }
                        @page { margin: 1.5cm; }
                    }
                `}
            </style>
        </Box>
    );
};

export default ReceiptDetailPage;
